import api from './api';

/**
 * Routing Policy Service
 *
 * API client for application-aware routing - Routing Policies & Rules
 * Phase 4.2: UI Integration
 */

// Routing Policy types
export interface RoutingPolicy {
  id: string;
  tenant_id: string;
  edge_id?: string;
  scope?: 'global' | 'edge' | 'device_group' | 'dg_template';
  source_template_id?: string | null;
  name: string;
  description?: string;
  priority: number;
  enabled: number;
  inspection_mode?: 'auto' | 'pre_inspection' | 'post_inspection';
  default_action?: 'allow' | 'deny';
  create_time?: string;
  update_time?: string;
  rules?: RoutingPolicyRule[];
}

export interface RoutingPolicyRule {
  id: string;
  policy_id: string;
  app_definition_id?: string;
  app_group_id?: string;
  source_subnet?: string;
  destination_subnet?: string;
  time_window?: string;
  action_type: 'route_via_interface' | 'route_via_nexthop' | 'route_via_tunnel' | 'drop' | 'rate_limit' | 'load_balance' | 'allow';
  action_target?: string;
  qos_profile?: 'realtime' | 'priority' | 'best_effort';
  bandwidth_limit?: number;
  rule_order: number;
  enabled: number;
  // Unified Policy Engine fields
  dscp_match?: string;
  dscp_mark?: number;
  load_balance_config?: LoadBalanceConfig;
  fallback_target?: string;
  requires_suricata?: number;
  create_time?: string;
  update_time?: string;
}

export interface LoadBalanceConfig {
  interfaces: { name: string; weight: number }[];
  algorithm: 'round_robin' | 'weighted' | 'random';
}

export interface PolicyListParams {
  search?: string;
  edgeId?: string;
  tenantId?: string;
  scope?: 'global' | 'edge' | 'device_group' | 'dg_template';
  page?: number;
  pageSize?: number;
}

export interface PolicyCreateData {
  tenant_id?: string;
  edge_id?: string;
  scope?: 'global' | 'device_group' | 'dg_template';
  name: string;
  description?: string;
  priority?: number;
  enabled?: number;
  inspection_mode?: 'auto' | 'pre_inspection' | 'post_inspection';
  default_action?: 'allow' | 'deny';
  rules?: RuleCreateData[];
}

export interface PolicyUpdateData {
  edge_id?: string | null;
  name?: string;
  description?: string;
  priority?: number;
  enabled?: number;
  inspection_mode?: 'auto' | 'pre_inspection' | 'post_inspection';
  default_action?: 'allow' | 'deny';
}

export interface RuleCreateData {
  app_definition_id?: string;
  app_group_id?: string;
  source_subnet?: string;
  destination_subnet?: string;
  time_window?: string;
  action_type: 'route_via_interface' | 'route_via_nexthop' | 'route_via_tunnel' | 'drop' | 'rate_limit' | 'load_balance' | 'allow';
  action_target?: string;
  qos_profile?: 'realtime' | 'priority' | 'best_effort';
  bandwidth_limit?: number;
  dscp_match?: string;
  dscp_mark?: number;
  fallback_target?: string;
  load_balance_config?: LoadBalanceConfig;
  rule_order?: number;
  enabled?: number;
}

export interface RuleUpdateData {
  app_definition_id?: string;
  app_group_id?: string;
  source_subnet?: string;
  destination_subnet?: string;
  time_window?: string;
  action_type?: 'route_via_interface' | 'route_via_nexthop' | 'route_via_tunnel' | 'drop' | 'rate_limit' | 'load_balance' | 'allow';
  action_target?: string;
  qos_profile?: 'realtime' | 'priority' | 'best_effort';
  bandwidth_limit?: number;
  dscp_match?: string;
  dscp_mark?: number;
  fallback_target?: string;
  load_balance_config?: LoadBalanceConfig;
  rule_order?: number;
  enabled?: number;
}

export interface PolicyValidationResult {
  valid: boolean;
  warnings: string[];
  errors: string[];
}

export interface RoutableSubnet {
  subnet: string;
  source: string;
  edgeNames: string[];
}

export interface PaginatedPolicyResponse {
  items: RoutingPolicy[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

// Edge Destinations — context-aware destination picker types
export interface EdgeDestinationInterface {
  name: string;          // port_name: "G0", "G1"
  label: string;         // "WAN-Ethernet (G0) — 192.168.0.251/24"
  role: 'WAN' | 'LAN';
  ipv4?: string;
  subnet?: string;       // network CIDR: "192.168.0.0/24"
}

export interface EdgeDestinationTunnel {
  name: string;          // "wg0", "wg1", "wg2", "wg3", "host-xfrm-apps"
  label: string;
  configured: boolean;
  protocol?: string;     // "wireguard" | "ikev2"
  peeringName?: string;
}

export interface EdgeDestinationPeer {
  id: string;
  edgeName: string;
  tunnelIp: string;
  role: string;
  status: string;
  subnets: string[];
  tunnelIndex: number;
}

export interface EdgeDestinationSubnet {
  subnet: string;
  source: string;
  sourceName?: string;
  peerName?: string;
}

export interface EdgeDestinationMeta {
  hasPeering: boolean;
  hasDualTunnel: boolean;
  hasAppVpn: boolean;
  appVpnProtocol?: 'wireguard' | 'ikev2';
  hasConnectors: boolean;
}

export interface EdgeDestinations {
  interfaces: EdgeDestinationInterface[];
  tunnels: EdgeDestinationTunnel[];
  peers: EdgeDestinationPeer[];
  subnets: EdgeDestinationSubnet[];
  meta: EdgeDestinationMeta;
}

export interface DgPolicyEdgeView {
  policy: {
    id: string;
    name: string;
    description?: string | null;
    priority: number;
    enabled: number;
    default_action: 'allow' | 'deny';
  };
  groups: { id: string; name: string; subnet_cidr: string | null }[];
  rules: {
    id: string;
    rule_order: number;
    action_type: string;
    destination_subnet?: string | null;
    app_definition_id?: string | null;
    app_group_id?: string | null;
    bandwidth_limit?: number | null;
    enabled: number;
    reachable: boolean | null;
    reachability_source?: string;
  }[];
}

const routingPolicyService = {
  /**
   * List all routing policies
   */
  list: async (params?: PolicyListParams) => {
    const response = await api.get('/routing-policies', { params });
    return response.data as PaginatedPolicyResponse;
  },

  /**
   * Get policy by ID
   */
  getById: async (id: string) => {
    const response = await api.get(`/routing-policies/${id}`);
    return response.data as RoutingPolicy;
  },

  /**
   * Create new policy
   */
  create: async (data: PolicyCreateData) => {
    const response = await api.post('/routing-policies', data);
    return response.data as RoutingPolicy;
  },

  /**
   * Update policy
   */
  update: async (id: string, data: PolicyUpdateData) => {
    const response = await api.put(`/routing-policies/${id}`, data);
    return response.data as RoutingPolicy;
  },

  /**
   * Delete policy
   */
  delete: async (id: string) => {
    await api.delete(`/routing-policies/${id}`);
  },

  /**
   * Validate policy
   */
  validate: async (id: string) => {
    const response = await api.get(`/routing-policies/${id}/validate`);
    return response.data as PolicyValidationResult;
  },

  /**
   * Add rule to policy
   */
  addRule: async (policyId: string, data: RuleCreateData) => {
    const response = await api.post(`/routing-policies/${policyId}/rules`, data);
    return response.data as RoutingPolicyRule;
  },

  /**
   * Update policy rule
   */
  updateRule: async (policyId: string, ruleId: string, data: RuleUpdateData) => {
    const response = await api.put(`/routing-policies/${policyId}/rules/${ruleId}`, data);
    return response.data as RoutingPolicyRule;
  },

  /**
   * Delete policy rule
   */
  deleteRule: async (policyId: string, ruleId: string) => {
    await api.delete(`/routing-policies/${policyId}/rules/${ruleId}`);
  },

  /**
   * Reorder policy rules
   */
  reorderRules: async (policyId: string, ruleOrders: { ruleId: string; order: number }[]) => {
    await api.post(`/routing-policies/${policyId}/rules/reorder`, { ruleOrders });
  },

  /**
   * Apply a policy template to an edge
   * Clones the template with edge_id set to the target edge
   */
  applyTemplate: async (templateId: string, edgeId: string) => {
    const response = await api.post(`/routing-policies/${templateId}/apply`, { edge_id: edgeId });
    return response.data as RoutingPolicy;
  },

  /**
   * Get routable subnets for a tenant's edges
   * Used by RuleModal destination_subnet dropdown for device-group policies
   */
  getRoutableSubnets: async (tenantId: string) => {
    const response = await api.get('/routing-policies/routable-subnets', { params: { tenantId } });
    return response.data as RoutableSubnet[];
  },

  /**
   * Get device-group policies that affect a specific edge (read-only view)
   */
  getDeviceGroupPoliciesForEdge: async (edgeId: string) => {
    const response = await api.get(`/routing-policies/edge/${edgeId}/device-group-policies`);
    return response.data as DgPolicyEdgeView[];
  },

  /**
   * Get context-aware destination options for an edge.
   * Returns interfaces, tunnels, peers, subnets, and meta flags.
   * Used by RuleModal for both edge routing policies and DG access policies.
   */
  getEdgeDestinations: async (edgeGuid: string) => {
    const response = await api.get(`/routing-policies/edges/${edgeGuid}/destinations`);
    return response.data as EdgeDestinations;
  },
};

export default routingPolicyService;
